// 首页区块链接
import {
  findMenuPathByMenuId,
  joinMenuPath,
  normalizeMenuPath,
  resolveMenuNodePath
} from '@/components/portal/common/utils/menuPath'
import { HOME_PATH } from '@/components/layout/utils/navMenu'
import { buildNewsDetailPath } from '@/components/modules/news/utils/newsPages'
import { findRootMenuByContentType, findRootMenuByTemplateId } from '@/components/portal/common/utils/menuPath'

/** 判断内容项是否匹配路由 key（slug 或 id） */
export function matchesContentRouteKey(item, routeKey) {
  if (!item || routeKey == null || routeKey === '') return false
  const key = String(routeKey)
  return item.slug === key || String(item.id) === key
}

/** 在内容列表中按路由 key 查找匹配项 */
export function findContentByRouteKey(items, routeKey) {
  return (items || []).find((item) => matchesContentRouteKey(item, routeKey)) || null
}

/** 解析内容项的路由 key（优先 id，其次 slug） */
export function resolveContentRouteKey(item) {
  if (!item) return ''
  return String(item.id || item.slug || '')
}

function resolveRootMenuPath(menus = [], contentType, templateId) {
  const byTemplate = templateId != null ? findRootMenuByTemplateId(menus, templateId) : null
  if (byTemplate) return normalizeMenuPath(byTemplate.path)
  const byType = findRootMenuByContentType(menus, contentType)
  return normalizeMenuPath(byType?.path)
}

function isNewsType(item, contentType) {
  const type = item?.type || contentType || ''
  return type === 'NEWS' || contentType === 'NEWS'
}

function isSolutionType(item, contentType) {
  const type = item?.type || contentType || ''
  return type === 'SOLUTION' || contentType === 'SOLUTION'
}

function findMenuNodeById(menus = [], menuId) {
  if (menuId == null || menuId === '') return null
  const target = String(menuId)
  const walk = (nodes = []) => {
    for (const node of nodes) {
      if (String(node.id) === target) return node
      const nested = walk(node.children)
      if (nested) return nested
    }
    return null
  }
  return walk(menus)
}

function isUsableNewsRoot(menu) {
  const path = resolveMenuNodePath(menu)
  return Boolean(path && path !== HOME_PATH)
}

/** 解析新闻栏目根菜单：优先从条目 menuId 上溯（避免新闻中心 / 投资者关系串台） */
export function resolveNewsRootMenu(item, menus = []) {
  let node = findMenuNodeById(menus, item?.menuId)
  if (node) {
    const byId = new Map()
    const index = (nodes = []) => {
      for (const current of nodes) {
        byId.set(String(current.id), current)
        index(current.children)
      }
    }
    index(menus)

    let newsAncestor = null
    let cursor = node
    while (cursor) {
      if (cursor.contentType === 'NEWS') newsAncestor = cursor
      if (cursor.parentId == null || cursor.parentId === '') break
      const parent = byId.get(String(cursor.parentId))
      if (!parent) break
      cursor = parent
    }
    // 取最上一级 NEWS 祖先，避免把分类节点（/news/company）当成详情根
    if (isUsableNewsRoot(newsAncestor)) return newsAncestor
  }

  const byType = findRootMenuByContentType(menus, 'NEWS')
  if (isUsableNewsRoot(byType)) return byType

  const byTemplate = findRootMenuByTemplateId(menus, 1)
  if (isUsableNewsRoot(byTemplate)) return byTemplate

  return null
}

/** 新闻详情链接：禁止用分类 path，避免首页点新闻只进列表 */
export function resolveNewsDetailLink(item, menus = [], rootMenu = null) {
  const routeKey = resolveContentRouteKey(item)
  if (!routeKey || routeKey === 'undefined') return ''

  const preferredRoot = isUsableNewsRoot(rootMenu) ? rootMenu : null
  const newsRoot = preferredRoot || resolveNewsRootMenu(item, menus)
  const detailPath = buildNewsDetailPath(item, newsRoot)
  if (detailPath && detailPath !== HOME_PATH) return detailPath

  // 兜底：分类菜单 path 的父级 + id（/news/company → /news/:id）
  const categoryPath = findMenuPathByMenuId(menus, item?.menuId)
  if (categoryPath && categoryPath !== HOME_PATH) {
    const parentPath = categoryPath.replace(/\/[^/]+$/, '')
    if (parentPath && parentPath !== HOME_PATH) {
      return joinMenuPath(parentPath, routeKey)
    }
  }

  return ''
}

/** 根据内容类型生成详情页路由链接 */
export function resolveItemLink(item, contentType, menus = []) {
  if (!item) return '/'
  const routeKey = resolveContentRouteKey(item)
  const type = item.type || contentType || ''

  // 新闻必须进详情；item.path 往往是分类菜单 path，不能直接用
  if (isNewsType(item, contentType)) {
    return resolveNewsDetailLink(item, menus) || HOME_PATH
  }

  // 解决方案进 content 详情；item.path 往往是分类菜单 path
  if (isSolutionType(item, contentType)) {
    const base = resolveRootMenuPath(menus, 'SOLUTION', 2)
    return base && routeKey ? joinMenuPath(base, routeKey) : '/'
  }

  if (item.path) return item.path

  if (type === 'INTRODUCTION' || contentType === 'INTRODUCTION') {
    if (item.menuId && menus.length) {
      const path = findMenuPathByMenuId(menus, item.menuId)
      if (path) return path
    }
    const base = resolveRootMenuPath(menus, 'INTRODUCTION', 3)
    return base && routeKey ? joinMenuPath(base, routeKey) : '/'
  }

  if (type === 'PRODUCT' || item.categoryId != null || contentType === 'PRODUCT_CATEGORY') {
    return `/products/detail/${item.id || routeKey}`
  }

  return resolveNewsDetailLink(item, menus) || HOME_PATH
}

/** 生成产品详情页路由链接 */
export function resolveProductLink(product, productsRootPath = '') {
  const id = product?.id
  if (!id) return normalizeMenuPath(productsRootPath) || '/'
  return `/products/detail/${id}`
}

/** 解析内容链接（新闻/方案始终重算详情路径，避免缓存了分类 link） */
export function resolveContentLink(item, contentType, menus = []) {
  if (isNewsType(item, contentType)) {
    return resolveItemLink(item, 'NEWS', menus)
  }
  if (isSolutionType(item, contentType)) {
    return resolveItemLink(item, 'SOLUTION', menus)
  }
  return item?.link || resolveItemLink(item, contentType, menus)
}
